import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import type { DefaultValues } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";
import { keys } from "@/lib/i18n/portal";
import { useFloatingActionBar } from "@/lib/hooks/useFloatingActionBar";
import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { toE164OrNull } from "@/lib/phone/phone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateProfileMutation } from "@/modules/profile/services/useUpdateProfileMutation";
import {
  Alert,
  DiscardChangesDialog,
  PhoneInputField,
  FormField,
  TextInput,
  SuccessNotice,
} from "@a2z/ui";
import { useLocale } from "@admin/app/locale";
import { getPhoneInputLabels } from "@/shared/components/forms/phone-input-labels";
import { FormActionButtons } from "./FormActionButtons";

export function ProfileForm({
  defaultValues,
}: {
  defaultValues: { name?: string; email?: string; phone?: string };
}) {
  const t = useTranslations();
  const locale = useLocale();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const formId = useId();
  const actionRef = useRef<HTMLDivElement | null>(null);
  const initialValues = useMemo<
    DefaultValues<{
      name: string;
      email: string;
      phone: string;
    }>
  >(
    () => ({
      name: defaultValues.name ?? "",
      email: defaultValues.email ?? "",
      phone: defaultValues.phone ?? "",
    }),
    [defaultValues.email, defaultValues.name, defaultValues.phone],
  );
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<{ name: string; email: string; phone: string }>({
    defaultValues: initialValues,
    mode: "onChange",
  });
  const [savedValues, setSavedValues] = useState(initialValues);
  const { mutateAsync: updateProfile } = useUpdateProfileMutation();
  useEffect(() => {
    reset(initialValues);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- default profile values can arrive after initial render
    setSavedValues(initialValues);
    setError(null);
    setSuccess(false);
  }, [initialValues, reset]);
  const watchedValues = useWatch({
    control,
    name: ["name", "email", "phone"],
  });

  const hasChanges = useMemo(() => {
    const name = watchedValues[0] ?? "";
    const email = watchedValues[1] ?? "";
    const phone = watchedValues[2] ?? "";
    return (
      name !== (savedValues.name ?? "") ||
      email !== (savedValues.email ?? "") ||
      phone !== (savedValues.phone ?? "")
    );
  }, [savedValues.email, savedValues.name, savedValues.phone, watchedValues]);
  const { promptDiscard, discardDialogState } = useFormSafety({
    isDirty: hasChanges,
    onDiscard: () => {
      reset(savedValues);
      setError(null);
      setSuccess(false);
    },
  });
  const actionsVisible = hasChanges || isSubmitting;
  const shouldFloatActions = useFloatingActionBar(actionRef, actionsVisible);

  const handleCancel = () =>
    promptDiscard(() => {
      reset(savedValues);
      setError(null);
      setSuccess(false);
    });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    setSuccess(false);
    const normalizedPhone =
      values.phone.trim().length === 0
        ? undefined
        : (toE164OrNull(values.phone) ?? undefined);
    if (values.phone.trim().length > 0 && !normalizedPhone) {
      setError(t(keys.buyer.trackingValidationPhone));
      return;
    }

    try {
      await updateProfile({
        ...values,
        phone: normalizedPhone,
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      return;
    }

    const nextValues = { ...values };
    setSavedValues(nextValues);
    reset(nextValues);
    setSuccess(true);
  });

  return (
    <>
      <form
        id={formId}
        onSubmit={onSubmit}
        className="mx-auto flex w-full max-w-[640px] flex-col gap-4"
      >
        {error ? (
          <Alert message={error} helperText={t(keys.errors.recovery)} />
        ) : null}
        {success ? (
          <SuccessNotice title={t(keys.common.updateSuccess)} />
        ) : null}
        <div className="flex flex-col gap-4 px-2 sm:px-3">
          <FormField
            label={t(keys.common.name)}
            name="name"
            error={errors.name?.message}
          >
            <TextInput
              placeholder={t(keys.placeholders.name)}
              hasError={Boolean(errors.name)}
              {...register("name")}
            />
          </FormField>
          <div className="grid gap-4 lg:grid-cols-2">
            <FormField
              label={t(keys.common.email)}
              name="email"
              error={errors.email?.message}
            >
              <TextInput
                type="email"
                placeholder={t(keys.placeholders.email)}
                hasError={Boolean(errors.email)}
                {...register("email")}
              />
            </FormField>
            <FormField
              label={t(keys.common.phone)}
              name="phone"
              error={errors.phone?.message}
            >
              <PhoneInputField
                name="phone"
                value={watchedValues[2] ?? ""}
                onChange={(value) =>
                  setValue("phone", value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                locale={locale}
                labels={getPhoneInputLabels(t)}
                hasError={Boolean(errors.phone)}
              />
            </FormField>
          </div>
          <div
            ref={actionRef}
            className={`mt-4 overflow-hidden transition-all duration-300 ${
              actionsVisible
                ? "max-h-24 translate-y-0 opacity-100"
                : "pointer-events-none max-h-0 -translate-y-1 opacity-0"
            }`}
            aria-hidden={!actionsVisible}
          >
            <div className={actionsVisible ? "pb-1" : "pb-0"}>
              <FormActionButtons
                cancelLabel={t(keys.common.cancel)}
                saveLabel={t(keys.common.update)}
                loadingLabel={t(keys.common.loading)}
                onCancel={handleCancel}
                isSaving={isSubmitting}
              />
            </div>
          </div>
        </div>
      </form>
      {shouldFloatActions && actionsVisible ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[var(--shadow-sm)]">
          <div className="mx-auto max-w-[640px]">
            <FormActionButtons
              formId={formId}
              cancelLabel={t(keys.common.cancel)}
              saveLabel={t(keys.common.update)}
              loadingLabel={t(keys.common.loading)}
              onCancel={handleCancel}
              isSaving={isSubmitting}
            />
          </div>
        </div>
      ) : null}
      <DiscardChangesDialog
        {...discardDialogState}
        title={t(keys.forms.discardChangesTitle)}
        body={t(keys.forms.discardChangesBody)}
        discardLabel={t(keys.forms.discardChangesDiscard)}
        continueLabel={t(keys.forms.discardChangesContinue)}
      />
    </>
  );
}
